/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
 * Developer note! When updating this file, make sure to also update the
 * _app-config template files in pwa-kit-create-app.
 *
 * In the pwa-kit-create-app, the templates are found under:
 * - assets/bootstrap/js/overrides/app/components/_app-config
 * - assets/templates/@salesforce/retail-react-app/app/components/_app-config
 */
import React from 'react';
import PropTypes from 'prop-types';

import {ReactQueryDevtools} from '@tanstack/react-query-devtools';

import {CommerceApiProvider} from '@salesforce/commerce-sdk-react';
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query';
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {ChakraProvider} from '@salesforce/retail-react-app/app/components/shared/ui';
import {DEFAULT_DNT_STATE} from '@salesforce/retail-react-app/app/constants';
import {MultiSiteProvider} from '@salesforce/retail-react-app/app/contexts';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import theme from '@salesforce/retail-react-app/app/theme';
import {resolveLocaleFromUrl, resolveSiteFromUrl} from '@salesforce/retail-react-app/app/utils/site-utils';
import {createUrlTemplate} from '@salesforce/retail-react-app/app/utils/url';

import {GlobalConfigProvider} from '@amblique/king-living/app/contexts/global-config-context'; // Save data of Contentful GlobalConfig into context

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible';

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
  const {correlationId} = useCorrelationId();
  const headers = {
    'correlation-id': correlationId,
  };

  const commerceApiConfig = locals.appConfig.commerceAPI;

  const appOrigin = useAppOrigin();

  const passwordlessCallback = locals.appConfig.login?.passwordless?.callbackURI;

  return (
    <CommerceApiProvider
      clientId={commerceApiConfig.parameters.clientId}
      currency={locals.locale?.preferredCurrency}
      defaultDnt={DEFAULT_DNT_STATE}
      enablePWAKitPrivateClient={true}
      headers={headers}
      locale={locals.locale?.id}
      logger={createLogger({packageName: 'commerce-sdk-react'})}
      organizationId={commerceApiConfig.parameters.organizationId}
      passwordlessLoginCallbackURI={passwordlessCallback}
      proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
      redirectURI={`${appOrigin}/callback`}
      shortCode={commerceApiConfig.parameters.shortCode}
      siteId={locals.site?.id}
    >
      <MultiSiteProvider buildUrl={locals.buildUrl} locale={locals.locale} site={locals.site}>
        <GlobalConfigProvider>
          <ChakraProvider theme={theme}>{children}</ChakraProvider>
        </GlobalConfigProvider>
      </MultiSiteProvider>
      <ReactQueryDevtools />
    </CommerceApiProvider>
  );
};

AppConfig.restore = (locals = {}) => {
  const path =
    typeof window === 'undefined' ?
      locals.originalUrl :
      `${window.location.pathname}${window.location.search}`;

  const site = resolveSiteFromUrl(path);
  const locale = resolveLocaleFromUrl(path);

  const {app: appConfig} = getConfig();
  const apiConfig = {
    ...appConfig.commerceAPI,
    einsteinConfig: appConfig.einsteinAPI,
  };

  apiConfig.parameters.siteId = site.id;

  locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id);
  locals.site = site;
  locals.locale = locale;
  locals.appConfig = appConfig;
};

AppConfig.freeze = () => undefined;

AppConfig.extraGetPropsArgs = (locals = {}) => {
  return {
    buildUrl: locals.buildUrl,
    site: locals.site,
    locale: locals.locale,
  };
};

AppConfig.propTypes = {
  children: PropTypes.node,
  locals: PropTypes.object,
};

const isServerSide = typeof window === 'undefined';
// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 10 * 1000,
        ...(isServerSide ? {retryOnMount: false} : {}),
      },
      mutations: {
        retry: false,
      },
    },
  },
  beforeHydrate: (data) => {
    const now = Date.now();

    // Helper to reset the data timestamp to time of app load.
    const updateQueryTimeStamp = ({state}) => {
      state.dataUpdatedAt = now;
    };

    // Update serialized mutations and queries to ensure that the cached data is
    // considered fresh on first load.
    data?.mutations?.forEach(updateQueryTimeStamp);
    data?.queries?.forEach(updateQueryTimeStamp);

    return data;
  },
};

export default withReactQuery(AppConfig, options);
